/* ═══════════════════════════════════════════════════════════════
   Project widget context — the CONTRACT every project-dashboard
   widget (T4+) builds against. Type-only + tiny pure helpers, no
   React components, so it can be imported from anywhere (Hub,
   Dashboard, the widget catalog, tests) without pulling in JSX.
   ═══════════════════════════════════════════════════════════════ */

import type * as React from 'react';
import type {
  Project,
  ProjectAssignment,
  TeamMember,
  BillingRole,
  ProjectPhase,
  Deliverable,
  Team,
  TeamAssignment,
  Organization,
  ProjectType,
  BillingType,
  ProjectScope,
  ProjectScopeAssignment,
  ProjectSpec,
  StandardConfig,
  ProjectDesignBrief,
  DesignBriefField,
} from '../../../../shared/types';
import type { Department } from '../../../../shared/entities';
import type { AuthUser } from '../../../services/authService';
import type { DeepLink } from '../../../types/navigation';
import type { WidgetDef } from '../../../components/WidgetGrid';
import { hasPermission } from '../../../../shared/permissions';

/** No formal `Forma` type exists yet — a project forma is a `StandardConfig`
 *  row (see shared/forma.ts). Aliased here so the contract can speak in
 *  product terms without inventing a duplicate type. */
export type Forma = StandardConfig;

/** Firm-wide default phase definition (admin-center Phases list) — not a
 *  full ProjectPhase row, just the seed shape used before a project has its
 *  own phase rows. Mirrors the old inline type in the pre-R1 Projects page. */
export interface FirmPhaseDef {
  id: string;
  name: string;
  sortOrder: number;
}

/** Copied verbatim from the pre-R1 dashboard-widgets module (shared.tsx:12-25) — the drill
 *  targets a project-dashboard widget can ask its host to honor (in-page
 *  scroll/focus or a cross-page `navigate`). */
export type DashboardDrill =
  | { kind: 'tab'; tab: 'specs' | 'dashboard' }
  | { kind: 'widget-focus'; widgetId: string }
  | { kind: 'invoice'; invoiceId: string }
  | { kind: 'invoice-list'; status?: 'overdue' | 'all' }
  | { kind: 'budget-editor'; phaseId?: string; billingRoleId?: string }
  | { kind: 'forecast-week'; weekStart: string }
  | { kind: 'time-entry-list'; weekStart?: string }
  | { kind: 'deliverable'; deliverableId: string }
  | { kind: 'phase'; phaseId: string }
  | { kind: 'task'; taskId: string }
  | { kind: 'new-task' }
  /** Builder R2 (Widgets R2 §D) — the Specs source's row drill. The stock
   *  Specs widget opens a project spec via `ctx.onOpenProjectSpec`, a
   *  callback outside this union; a generic custom widget has no access to
   *  that callback, so this kind lets its consumer (T4b) route the same
   *  way through `onDrill`. */
  | { kind: 'spec'; specId: string };

/** Cross-page navigation function — same shape app.tsx already passes down
 *  as an inline type; named here so every consumer imports one symbol. */
export type NavigateFn = (link: DeepLink) => void;

/** One firm-scoped or project-scoped data source, loaded independently so a
 *  slow/failed source degrades to its own error state instead of blanking
 *  the whole dashboard (Promise.allSettled discipline, DESIGN.md data law). */
export interface SliceState<T> {
  data: T;
  status: 'loading' | 'ready' | 'error';
  error?: string;
  reload: () => Promise<void>;
}

/** Which part of the dashboard a caller wants focused when it opens a
 *  project — a specific widget scrolled into view, or a full-bleed
 *  takeover (e.g. the Specs tab, reborn as a widget takeover). */
export type DashboardFocus =
  | { kind: 'widget'; widgetId: string }
  | { kind: 'takeover'; widgetId: string };

/** Everything a project-dashboard widget needs to render and act — data,
 *  derived permission booleans, and every mutation it's allowed to call.
 *  Built by Dashboard.tsx (T4) per open project; the type lives here so
 *  widget files (and the hub, and tests) can depend on the shape without
 *  depending on Dashboard.tsx's implementation. */
export interface ProjectWidgetCtx {
  project: Project;
  authUser: AuthUser | null | undefined;
  userPermissions: string[];
  /** Derived once from the permission constants at the old combined
   *  Projects page's :110-135 —
   *  same keys, same meaning, so a widget never re-derives its own gate. */
  can: {
    edit: boolean;
    viewFinancials: boolean;
    assignTeam: boolean;
    /** `projects.assign_anyone` — bypasses the org-chart restriction
     *  `assignTeam` is subject to (Decision 11). Used to decide whether
     *  the "+ Add person" picker offers everyone or only subordinates. */
    assignAnyone: boolean;
    deleteProject: boolean;
    createProject: boolean;
    viewCompile: boolean;
    exportProjectSpecs: boolean;
    deleteSpecs: boolean;
    archiveSpecs: boolean;
    /** Close / Reopen project (`projects.archive`). Never ANDed with
     *  `locked` — it is the key that flips `locked` itself, so a closed
     *  project must keep it to show "Reopen project". */
    archiveProject: boolean;
    /** Complete / reopen a phase (`admin_center.manage_billing`) — a
     *  completed phase locks time against it. */
    manageBilling: boolean;
  };
  /** `project.status === 'closed'`. Every mutation `can.*` bool above is
   *  already ANDed with `!locked` (see `deriveCan`) — widgets read this
   *  directly only when they need to explain WHY a control is gone (the
   *  locked pill, the Status field's confirm gate) rather than just hide it. */
  locked: boolean;
  members: TeamMember[];
  billingRoles: BillingRole[];
  teams: Team[];
  teamAssignments: TeamAssignment[];
  departments: Department[];
  orgs: Organization[];
  projectTypes: ProjectType[];
  billingTypes: BillingType[];
  firmPhases: FirmPhaseDef[];
  allScopes: ProjectScope[];

  assignments: SliceState<ProjectAssignment[]>;
  phases: SliceState<ProjectPhase[]>;
  deliverables: SliceState<Deliverable[]>;
  scopeAssignments: SliceState<ProjectScopeAssignment[]>;
  projectSpecs: SliceState<ProjectSpec[]>;
  formas: SliceState<Forma[]>;
  designBrief: SliceState<ProjectDesignBrief | null>;
  /** Migration 055 — custom title/value rows a PM adds to the brief,
   *  beyond its fixed columns. In `sortOrder`. */
  designBriefFields: SliceState<DesignBriefField[]>;

  saveProject(p: Project): Promise<Project>;
  saveDeliverable(d: Deliverable): Promise<void>;
  deleteDeliverable(id: string): Promise<void>;
  /** ONE phase write path (T3b) — sets `project.currentPhaseId` by id.
   *  Used by the Phase stepper, the Project widget, and the project form.
   *  Replaces the old name-based, status-cycling phase write path. */
  setCurrentPhase(phaseId: string): Promise<void>;
  savePhase(p: ProjectPhase): Promise<void>;
  deletePhase(id: string): Promise<void>;
  reorderPhases(orderedIds: string[]): Promise<void>;
  assignMember(memberId: string, role: string): Promise<void>;
  assignTeam(teamId: string): Promise<void>;
  setAssignmentRole(assignmentId: string, role: string): Promise<void>;
  removeAssignment(assignmentId: string): Promise<void>;
  saveDesignBrief(b: ProjectDesignBrief): Promise<void>;
  /** Create or update ONE custom brief field; returns the saved row so a
   *  caller that just created one (id: '') learns its server-assigned id. */
  saveDesignBriefField(f: DesignBriefField): Promise<DesignBriefField>;
  removeDesignBriefField(id: string): Promise<void>;
  reorderDesignBriefFields(orderedIds: string[]): Promise<void>;

  onDrill(target: DashboardDrill): void;
  navigate?: NavigateFn;
  /** `review: true` opens the section straight into Review (master updates). */
  onOpenProjectSpec?(specId: string, projectId: string, opts?: { review?: boolean }): void;
  /** `review: true` opens the project forma straight into Review (its
   *  master updates, SYNC_PLAN §3.2). */
  onOpenForma?(formaId: string, opts?: { review?: boolean }): void;
  openTakeover(widgetId: string): void;
  closeTakeover(): void;
  toast: (msg: string, isError?: boolean) => void;
  isWeb: boolean;
}

/** Pure derivation of `ProjectWidgetCtx.can` from a raw permission array —
 *  extracted from Dashboard.tsx (T3b permission audit) so it is importable
 *  and unit-testable without pulling in the whole page. Same nine keys,
 *  same meaning, behaviour identical to the pre-extraction inline useMemo. */
export function deriveCan(perms: string[] | undefined, locked = false): ProjectWidgetCtx['can'] {
  // Every MUTATION bool is `perm && !locked` — one place (Josh 2026-09-28:
  // closed = fully locked). View-only bools (viewFinancials, viewCompile,
  // exportProjectSpecs — reads/output, not a write) and archiveProject
  // (the key that flips `locked` itself) are deliberately NOT gated here.
  const gate = (v: boolean) => v && !locked;
  return {
    edit: gate(hasPermission(perms, 'projects.edit')),
    viewFinancials: hasPermission(perms, 'projects.view_financials'),
    assignTeam: gate(
      hasPermission(perms, 'projects.assign_team') || hasPermission(perms, 'projects.assign_anyone')
    ),
    assignAnyone: gate(hasPermission(perms, 'projects.assign_anyone')),
    deleteProject: gate(hasPermission(perms, 'projects.delete')),
    createProject: hasPermission(perms, 'projects.create'),
    viewCompile: hasPermission(perms, 'compile.view'),
    exportProjectSpecs: hasPermission(perms, 'project_specs.export'),
    deleteSpecs: gate(hasPermission(perms, 'specs.delete')),
    archiveSpecs: gate(hasPermission(perms, 'project_specs.archive')),
    archiveProject: hasPermission(perms, 'projects.archive'),
    manageBilling: gate(hasPermission(perms, 'admin_center.manage_billing')),
  };
}

/** A project-dashboard widget def — the same shape as the generic
 *  `WidgetDef<Ctx>` (WidgetGrid.tsx) plus an optional full-bleed takeover
 *  render (e.g. the Specs tab, which needs the whole surface, not a card). */
export interface ProjectWidgetDef extends WidgetDef<ProjectWidgetCtx> {
  takeover?: (ctx: ProjectWidgetCtx) => React.ReactNode;
  takeoverTitle?: string;
}
